import { UserRatingEdgeDto, UserRatingValue } from "@/api/generated/generatedTypes";
import { StagingGroup } from "../StagingGroupCard/types";

export type RatingPair = {
  raterId: string;
  ratedUserId: string;
};

export type GroupRatingVerdict = {
  likedPairs: RatingPair[];
  dislikedPairs: RatingPair[];
};

/**
 * Two members and how they rated each other, collapsed from the individual edges.
 *
 * `isMutual` means both sides landed on the same value, which is a far stronger signal
 * than one person's opinion. For a mutual relation the direction carries no meaning, so
 * the two ids are stored in a stable order rather than whichever edge arrived first.
 */
export type RatingRelation = {
  raterId: string;
  ratedUserId: string;
  isMutual: boolean;
};

export type GroupRatingRelations = {
  likedRelations: RatingRelation[];
  dislikedRelations: RatingRelation[];
};

/**
 * The groupmates one member rated, by value. Only ratings this member gave, because these
 * are rendered on that member's own row: the row says who is doing the rating and the chip
 * says who they rated, so no direction marker is needed.
 */
export type MemberRatingsGiven = {
  liked: string[];
  disliked: string[];
};

const EMPTY_VERDICT: GroupRatingVerdict = { likedPairs: [], dislikedPairs: [] };

const PAIR_KEY_SEPARATOR = "|";

/**
 * The uids of everyone staged in a group. Both the group card and each staged row
 * derive their query key from this, so they share one cached request.
 */
export const getMemberUids = (stagingGroup: StagingGroup): string[] =>
  stagingGroup.stagedRequests.map((request) => request.user.uid);

/**
 * Every liked and disliked rating between members of the group.
 *
 * Direction is ignored: if A rated B as disliked, that is a conflict regardless of what
 * B thought of A. NOT_SURE carries no signal and is dropped. A group can surface both
 * verdicts at once, since one pair getting along says nothing about another.
 *
 * Edges are re-filtered against the current members even though the API already scopes
 * them, because a cached response from a previous member set can still be on screen
 * while a new one is in flight.
 */
export const getGroupRatingVerdict = (
  memberUids: string[],
  edges: UserRatingEdgeDto[]
): GroupRatingVerdict => {
  if (memberUids.length < 2) {
    return EMPTY_VERDICT;
  }

  const memberSet = new Set(memberUids);
  const likedPairs: RatingPair[] = [];
  const dislikedPairs: RatingPair[] = [];

  for (const edge of edges) {
    if (!memberSet.has(edge.raterId) || !memberSet.has(edge.ratedUserId)) {
      continue;
    }

    const pair = { raterId: edge.raterId, ratedUserId: edge.ratedUserId };

    if (edge.value === UserRatingValue.LIKED) {
      likedPairs.push(pair);
    }

    if (edge.value === UserRatingValue.DISLIKED) {
      dislikedPairs.push(pair);
    }
  }

  return { likedPairs, dislikedPairs };
};

/**
 * The same ratings as getGroupRatingVerdict, but one entry per pair of members instead of
 * one per rating. This is what the group card shows, because counting raw edges makes a
 * single mutual dislike read the same as two unrelated one-way dislikes, and double
 * counts a pair who have now met at more than one event.
 */
export const getGroupRatingRelations = (
  memberUids: string[],
  edges: UserRatingEdgeDto[]
): GroupRatingRelations => {
  const { likedPairs, dislikedPairs } = getGroupRatingVerdict(memberUids, edges);

  return {
    likedRelations: toRelations(likedPairs),
    dislikedRelations: toRelations(dislikedPairs),
  };
};

/**
 * Which groupmates this member rated. Returning uids rather than counts lets the chips name
 * them, which is what makes the direction readable without a glyph.
 */
export const getMemberRatingsGiven = (
  uid: string,
  memberUids: string[],
  edges: UserRatingEdgeDto[]
): MemberRatingsGiven => {
  const { likedPairs, dislikedPairs } = getGroupRatingVerdict(memberUids, edges);

  return {
    liked: toRatedCounterparts(uid, likedPairs),
    disliked: toRatedCounterparts(uid, dislikedPairs),
  };
};

const toRelations = (pairs: RatingPair[]): RatingRelation[] => {
  const pairsByCouple = new Map<string, RatingPair[]>();

  for (const pair of pairs) {
    const key = [pair.raterId, pair.ratedUserId].sort().join(PAIR_KEY_SEPARATOR);
    const existing = pairsByCouple.get(key);

    if (existing) {
      existing.push(pair);
      continue;
    }

    pairsByCouple.set(key, [pair]);
  }

  return Array.from(pairsByCouple.values()).map((couplePairs) => {
    // Counting entries would not work here: a pair who met at two events can produce two
    // ratings pointing the same way, which is one opinion rather than a mutual one.
    const directions = new Set(
      couplePairs.map((pair) => `${pair.raterId}${PAIR_KEY_SEPARATOR}${pair.ratedUserId}`)
    );
    const isMutual = directions.size > 1;
    const [first] = couplePairs;

    if (isMutual) {
      const [raterId, ratedUserId] = [first.raterId, first.ratedUserId].sort();
      return { raterId, ratedUserId, isMutual };
    }

    return { raterId: first.raterId, ratedUserId: first.ratedUserId, isMutual };
  });
};

// A pair who dined together more than once can produce several ratings pointing the same
// way, which is still one opinion about one person.
const toRatedCounterparts = (uid: string, pairs: RatingPair[]): string[] =>
  Array.from(new Set(pairs.filter((pair) => pair.raterId === uid).map((pair) => pair.ratedUserId)));
